import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useGetUsedEmojisQuery } from '../../aotd/api/artOfTheDayApi';
import {
  useKeepCardsMutation,
  usePlayCardMutation,
  useResolveCardOfferMutation,
} from '../api/respectCardsApi';
import { useAotdCardPlay } from '../aotd/AotdCardPlayContext';
import { handErrorText, knownPlayErrorCopy, resolvePlayError } from '../lib/playErrors';
import type { GetMyCardsResponse } from '../types/cardTypes';
import CardDetail from './CardDetail';
import { cardOriginLine } from '../lib/cardOrigin';
import HandCard from './HandCard';
import NewCardModal from './NewCardModal';
import OfferChooser, { offerFace, offerReason } from './OfferChooser';
import ReclaimPicker from './ReclaimPicker';
import { BRICK, CardSlot as Slot, DECK_INK, Note as Pen, Panel, PlayButton, SERIF, Title, useSize } from './deck';
import {
  AOTD_HAND_ID,
  assignSlots,
  DECK_SIZE,
  handAction,
  HandCardDef,
  HandEntry,
  handFrom,
  PLAY_WINDOW_MS,
  playableNow,
  SECOND_GUESS_EFFECT,
} from './handModel';
import { markNewCardSeen, wasNewCardSeen } from './newCardSeen';
import { newCardWhenLine, WINDOW_RULE, windowClock, windowLine } from './playWindowCopy';

/**
 * Your cards on the AOTD page, under the quiz. Data: `GET /cards/my-cards`
 * (in_hand copies, server playability, offers, the hand of 4, the play
 * window), shared with the page's Play modal through RTK.
 *
 * A deck of DECK_SIZE playing cards, fanned like a hand. Tap one to lift it
 * and read it; "play it" shows when the server says it can be played, which is
 * only inside the play window (unlock to +45 min), and those cards wear a
 * "play now" tab in the fan. A played card flies off and leaves a stamp. A
 * card that needs a target opens the page's Play modal; Reclamation asks for
 * its emojis first. A new card waits as an offer: keep it or throw it, and
 * with a full deck pick the card to swap out and confirm (`OfferChooser`).
 * Arriving on the page with an offer waiting opens the new-card modal once.
 * A deck over the limit asks for the 4 you keep.
 */

const Wrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 720px;
  min-width: 0;
  margin: 18px auto 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 0.75rem;
  font-style: italic;
  color: #9ca3af;
  margin-bottom: 2px;
`;

const WindowLine = styled.div<{ $open: boolean }>`
  font-size: 0.75rem;
  font-style: italic;
  line-height: 1.35;
  color: ${(p) => (p.$open ? BRICK : '#9ca3af')};
  margin-bottom: 6px;
`;

const Burn = styled.div`
  height: 2px;
  background: #f3f4f6;
  margin-bottom: 14px;
  overflow: hidden;
`;

const Fan = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding: 10px 0 12px;
`;

const StampBox = styled(motion.div)`
  position: absolute;
  left: 50%;
  top: 38%;
  color: ${BRICK};
  white-space: nowrap;
`;

/** The "played" mark left in an emptied slot. */
const Stamp: FC<{ reduce: boolean | null }> = ({ reduce }) => (
  <StampBox
    initial={reduce ? { opacity: 0, x: '-50%' } : { opacity: 0, scale: 1.6, x: '-50%' }}
    animate={{ opacity: 1, scale: 1, x: '-50%' }}
    transition={{ delay: 0.35, type: 'spring', stiffness: 500, damping: 18 }}
    style={{ fontFamily: SERIF, fontStyle: 'italic', fontWeight: 600, fontSize: 17, borderBottom: `1.5px solid ${BRICK}`, padding: '0 2px' }}
  >
    played
  </StampBox>
);

const Detail = styled(motion.div)`
  position: relative;
  margin-top: 10px;
`;

const Prompt = styled(motion.div)`
  margin-bottom: 12px;
`;

const PLAY_NOW = 'play now';

function blockedLine(code: string | undefined): string {
  if (code === 'outside_play_window') return WINDOW_RULE;
  // an unknown code is a server token, not something to show a player
  return knownPlayErrorCopy(code)?.hint ?? "it can't be played right now.";
}

interface Line {
  text: string;
  error?: boolean;
}

interface AotdHandProps {
  data: GetMyCardsResponse;
  aotdId: string | null;
  /** Re-read /my-cards (the play window turned over). */
  onRefresh: () => void;
}

const AotdHand: FC<AotdHandProps> = ({ data, aotdId, onRefresh }) => {
  const reduce = useReducedMotion();
  const [wrapRef, wrapSize] = useSize<HTMLDivElement>();
  const { openCardPlay, playableFocusRequest = 0 } = useAotdCardPlay();
  const [playCard, { isLoading: playing }] = usePlayCardMutation();
  const [resolveOffer, { isLoading: resolving }] = useResolveCardOfferMutation();
  const [keepCards, { isLoading: keeping }] = useKeepCardsMutation();

  // Copies played, thrown or returned from here leave the fan at once, and a
  // sorted offer or keep-4 picker closes at once, before /my-cards refetches
  // (a second tap in that gap would only earn an "already sorted" refusal).
  const [gone, setGone] = useState<ReadonlySet<string>>(() => new Set());
  const [doneOffers, setDoneOffers] = useState<ReadonlySet<string>>(() => new Set());
  const [keptFor, setKeptFor] = useState<GetMyCardsResponse | null>(null);
  const entries = useMemo(() => handFrom(data).filter((e) => !gone.has(e.instanceId)), [data, gone]);
  const byId = useMemo(() => new Map(entries.map((e) => [e.instanceId, e])), [entries]);
  const offers = (data.offers ?? []).filter((o) => !doneOffers.has(o.id));
  const offer = offers[0] ?? null;
  const overLimit = (data.hand?.overLimit ?? entries.length > DECK_SIZE) && keptFor !== data;
  const held = data.hand ? Math.min(data.hand.inHandCount, entries.length) : entries.length;

  // a card keeps its slot across refetches; a played card's slot keeps its stamp
  const [played, setPlayed] = useState<Record<number, HandCardDef>>({});
  const stamped = useMemo(() => new Set(Object.keys(played).map(Number)), [played]);
  const slotsRef = useRef<(string | null)[]>([]);
  const idsKey = entries.map((e) => e.instanceId).join(',');
  const slots = useMemo(() => {
    const next = assignSlots(slotsRef.current, idsKey ? idsKey.split(',') : [], stamped);
    slotsRef.current = next;
    return next;
  }, [idsKey, stamped]);

  const [selected, setSelected] = useState<number | null>(null);
  const [reclaiming, setReclaiming] = useState<{ slot: number; entry: HandEntry; max: number } | null>(null);
  const [line, setLine] = useState<Line | null>(null);
  const [keep, setKeep] = useState<string[]>([]);

  // the play window clock
  const win = data.playWindow;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  // the window turning over changes every card's playability: read it again
  const clock = windowClock(win, now);
  const { open, left, turnedOver } = clock;
  const askedRef = useRef(false);
  useEffect(() => {
    if (!turnedOver) {
      askedRef.current = false;
      return;
    }
    if (askedRef.current) return;
    askedRef.current = true;
    onRefresh();
  }, [turnedOver, onRefresh]);

  // The new-card modal: opens when you arrive with an offer waiting, once per
  // offer per visit. An offer that comes in later waits in the hand.
  const [modalOfferId, setModalOfferId] = useState<string | null>(() =>
    offer && !overLimit && !wasNewCardSeen(offer.id) ? offer.id : null,
  );
  useEffect(() => {
    if (modalOfferId) markNewCardSeen(modalOfferId);
  }, [modalOfferId]);

  // the waiting screens' cards button asks for the first card you can play now
  const focusSeenRef = useRef(playableFocusRequest);
  useEffect(() => {
    if (playableFocusRequest === focusSeenRef.current) return;
    focusSeenRef.current = playableFocusRequest;
    const i = slots.slice(0, DECK_SIZE).findIndex((id) => {
      const e = id ? byId.get(id) : undefined;
      return e ? playableNow(e, open) : false;
    });
    if (i < 0) return;
    setReclaiming(null);
    setSelected(i);
  }, [playableFocusRequest, slots, byId, open]);

  const wide = wrapSize.w > 600;
  const cw = wide ? 104 : 74;
  const ch = Math.round(cw * 1.4);
  const fanAngles = [-9, -3, 3, 9];
  const fanDrop = [12, 2, 2, 12];

  // nothing to show yet: no cards, no offer, nothing played here
  const shownRef = useRef(false);
  if (entries.length > 0 || offer || Object.keys(played).length > 0) shownRef.current = true;
  if (!shownRef.current) return null;

  const playEntry = async (slot: number, entry: HandEntry, emojis?: string[]) => {
    try {
      await playCard({
        cardInstanceId: entry.instanceId,
        ...(aotdId ? { aotdId } : {}),
        ...(emojis ? { emojis } : {}),
      }).unwrap();
      const card = entry.card;
      setGone((g) => new Set(g).add(entry.instanceId));
      setPlayed((p) => ({ ...p, [slot]: card }));
      setSelected(null);
      setReclaiming(null);
      if (entry.definition.effect?.type === SECOND_GUESS_EFFECT || card.slug === 'second-guess') {
        setLine({ text: 'your guess box is open again. the better of your two guesses counts.' });
      } else if (emojis) {
        setLine({ text: `got back ${emojis.join(' ')}. you can stage them again.` });
      } else {
        setLine({ text: `${card.emoji} ${card.name} played. it acts: ${card.when}.` });
      }
    } catch (err) {
      const { title, hint, staleInstance } = resolvePlayError(err);
      setLine({ text: `${title}. ${hint}`, error: true });
      if (staleInstance) {
        setSelected(null);
        setReclaiming(null);
        onRefresh();
      }
    }
  };

  const startPlay = (slot: number) => {
    const id = slots[slot];
    const entry = id ? byId.get(id) : undefined;
    if (!entry || playing) return;
    const action = handAction(entry);
    setLine(null);
    if (action.kind === 'target') {
      // the page's shared Play modal has the target picker
      openCardPlay(entry.instance, entry.playability);
      setSelected(null);
    } else if (action.kind === 'select') {
      setReclaiming({ slot, entry, max: action.max });
    } else if (action.kind === 'play') {
      void playEntry(slot, entry);
    }
  };

  const resolve = async (body: { keep: true; discardInstanceId?: string } | { keep: false }) => {
    if (!offer || resolving) return;
    const face = offerFace(offer);
    const thrown = body.keep && body.discardInstanceId ? byId.get(body.discardInstanceId)?.card : null;
    setLine(null);
    try {
      await resolveOffer({ offerId: offer.id, ...body }).unwrap();
      setDoneOffers((s) => new Set(s).add(offer.id));
      if (body.keep && body.discardInstanceId) {
        const thrownId = body.discardInstanceId;
        setGone((g) => new Set(g).add(thrownId));
      }
      setLine({
        text: !body.keep
          ? `threw away ${face.emoji} ${face.name}.`
          : thrown
            ? `kept ${face.emoji} ${face.name}, threw away ${thrown.emoji} ${thrown.name}.`
            : `kept ${face.emoji} ${face.name}.`,
      });
    } catch (err) {
      setLine({ text: handErrorText(err), error: true });
    }
  };

  const submitKeep = async () => {
    if (keep.length !== DECK_SIZE || keeping) return;
    try {
      const res = await keepCards({ instanceIds: keep }).unwrap();
      const returned = res.returned ?? [];
      setGone((g) => {
        const next = new Set(g);
        returned.forEach((id) => next.add(id));
        return next;
      });
      setKeptFor(data);
      setKeep([]);
      setLine({ text: 'kept your 4. the rest went back to the house.' });
    } catch (err) {
      setLine({ text: handErrorText(err), error: true });
    }
  };

  const selId = selected !== null ? slots[selected] : null;
  const sel = selId ? byId.get(selId) ?? null : null;
  const selAction = sel ? handAction(sel) : null;
  const full = entries.length >= DECK_SIZE;
  const keepOffer = (discardInstanceId?: string) =>
    void resolve(discardInstanceId ? { keep: true, discardInstanceId } : { keep: true });
  const throwOffer = () => void resolve({ keep: false });
  const modalOpen = Boolean(offer && modalOfferId === offer.id && !overLimit);

  return (
    <Wrap ref={wrapRef} id={AOTD_HAND_ID}>
      <Header>
        <span>your cards</span>
        <span>
          {held}/{DECK_SIZE}
        </span>
      </Header>
      <WindowLine $open={open}>{windowLine(clock, now)}</WindowLine>
      <Burn>
        {open && (
          <div style={{ height: '100%', width: `${(left / PLAY_WINDOW_MS) * 100}%`, background: BRICK, transition: 'width 1s linear' }} />
        )}
      </Burn>

      <AnimatePresence>
        {overLimit ? (
          <Prompt
            key="keep"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
          >
            <Panel style={{ padding: 14 }}>
              <Title size={17}>your deck holds {DECK_SIZE} now</Title>
              <Pen>
                you have {entries.length}. tap the {DECK_SIZE} you keep, the rest go back to the house.
              </Pen>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                {entries.map((e) => {
                  const on = keep.includes(e.instanceId);
                  return (
                    <Slot
                      key={e.instanceId}
                      type="button"
                      onClick={() =>
                        setKeep((k) => (on ? k.filter((x) => x !== e.instanceId) : k.length >= DECK_SIZE ? k : [...k, e.instanceId]))
                      }
                      aria-pressed={on}
                      aria-label={on ? `${e.card.name}, kept` : `keep ${e.card.name}`}
                      animate={reduce ? undefined : { y: on ? -6 : 0 }}
                      style={{ opacity: on || keep.length < DECK_SIZE ? 1 : 0.55 }}
                    >
                      <HandCard card={e.card} w={wide ? 84 : 58} h={wide ? 118 : 82} />
                      {on && (
                        <span
                          aria-hidden="true"
                          style={{ position: 'absolute', inset: -3, borderRadius: 9, border: `1.5px solid ${BRICK}`, pointerEvents: 'none' }}
                        />
                      )}
                    </Slot>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                <Pen color={keep.length === DECK_SIZE ? BRICK : undefined} size={17}>
                  {keep.length} / {DECK_SIZE}
                </Pen>
                <PlayButton type="button" disabled={keep.length !== DECK_SIZE || keeping} onClick={() => void submitKeep()}>
                  keep these {DECK_SIZE}
                </PlayButton>
              </div>
            </Panel>
          </Prompt>
        ) : offer ? (
          <Prompt
            key={`offer-${offer.id}`}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
          >
            <Panel style={{ padding: 14 }}>
              <Title size={17}>
                new card! {offerFace(offer).emoji} {offerFace(offer).name}
              </Title>
              {offerReason(offer) && <Pen size={15}>{offerReason(offer)}</Pen>}
              <div style={{ marginTop: 10 }}>
                <OfferChooser
                  key={offer.id}
                  offer={offer}
                  held={entries}
                  full={full}
                  wide={wide}
                  busy={resolving}
                  layout="inline"
                  onKeep={keepOffer}
                  onThrow={throwOffer}
                />
              </div>
            </Panel>
          </Prompt>
        ) : null}
      </AnimatePresence>

      {!overLimit && (
        <Fan>
          {slots.slice(0, DECK_SIZE).map((id, i) => {
            const entry = id ? byId.get(id) ?? null : null;
            const card = entry?.card ?? null;
            const isSel = selected === i;
            const stamp = played[i];
            const playNow = entry ? playableNow(entry, open) : false;
            return (
              <Slot
                key={i}
                type="button"
                disabled={!card}
                onClick={() => {
                  if (!card) return;
                  setReclaiming(null);
                  setSelected(isSel ? null : i);
                }}
                aria-pressed={isSel}
                aria-label={
                  card ? `${card.name}, ${card.when}${playNow ? ', you can play it now' : ''}` : stamp ? `${stamp.name}, played` : 'empty slot'
                }
                style={{ marginLeft: i === 0 ? 0 : wide ? 4 : -6, zIndex: isSel ? 5 : 1, cursor: card ? 'pointer' : 'default' }}
                animate={
                  reduce
                    ? undefined
                    : { rotate: isSel ? 0 : fanAngles[i], y: isSel ? -16 : fanDrop[i], scale: isSel ? 1.06 : 1 }
                }
                whileHover={reduce || !card || isSel ? undefined : { y: fanDrop[i] - 5 }}
                transition={{ type: 'spring', stiffness: 320, damping: 24 }}
              >
                <AnimatePresence mode="popLayout" initial={false}>
                  {card && entry ? (
                    <motion.div
                      key={entry.instanceId}
                      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24, rotate: -8 }}
                      animate={{ opacity: 1, y: 0, rotate: 0 }}
                      exit={reduce ? { opacity: 0 } : { opacity: 0, y: -90, rotate: 14, scale: 0.9, transition: { duration: 0.45, ease: 'easeIn' } }}
                    >
                      <HandCard card={card} w={cw} h={ch} mark={playNow ? PLAY_NOW : undefined} />
                    </motion.div>
                  ) : (
                    <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                      <HandCard card={null} w={cw} h={ch} note={stamp ? ' ' : undefined} />
                      {stamp && <Stamp reduce={reduce} />}
                    </motion.div>
                  )}
                </AnimatePresence>
              </Slot>
            );
          })}
        </Fan>
      )}

      <AnimatePresence mode="wait">
        {reclaiming ? (
          <Detail key="reclaim" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <ReclaimFlow
              max={reclaiming.max}
              busy={playing}
              onCancel={() => setReclaiming(null)}
              onDone={(picked) => void playEntry(reclaiming.slot, reclaiming.entry, picked)}
            />
          </Detail>
        ) : sel && !overLimit ? (
          <Detail
            key={`detail-${sel.instanceId}`}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
          >
            <CardDetail card={sel.card} wide={wide} origin={cardOriginLine(sel.instance)}>
              {selAction && (selAction.kind === 'play' || selAction.kind === 'target' || selAction.kind === 'select') ? (
                <PlayButton type="button" disabled={playing} onClick={() => startPlay(selected as number)}>
                  play it
                </PlayButton>
              ) : selAction?.kind === 'blocked' ? (
                <Pen>{blockedLine(selAction.code)}</Pen>
              ) : null}
            </CardDetail>
          </Detail>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {line && (
          <motion.div key={line.text} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ textAlign: 'center', marginTop: 8 }}>
            <Pen color={line.error ? BRICK : DECK_INK} size={line.error ? 16 : 19}>
              {line.text}
            </Pen>
          </motion.div>
        )}
      </AnimatePresence>

      {offer && (
        <NewCardModal
          open={modalOpen}
          onClose={() => setModalOfferId(null)}
          offer={offer}
          held={entries}
          full={full}
          busy={resolving}
          whenLine={newCardWhenLine(clock, now)}
          error={modalOpen && line?.error ? line.text : null}
          onKeep={keepOffer}
          onThrow={throwOffer}
        />
      )}
    </Wrap>
  );
};

/** Reclamation's picker over the player's used emojis (`GET /art-of-the-day/used-emojis`). */
const ReclaimFlow: FC<{ max: number; busy: boolean; onCancel: () => void; onDone: (picked: string[]) => void }> = ({
  max,
  busy,
  onCancel,
  onDone,
}) => {
  const { data, isLoading } = useGetUsedEmojisQuery(undefined, { refetchOnMountOrArgChange: true });
  const used = useMemo(() => {
    const rows = (data as { usedEmojis?: Array<{ emoji?: unknown }> } | undefined)?.usedEmojis ?? [];
    const seen = new Set<string>();
    return rows
      .map((r) => (typeof r.emoji === 'string' ? r.emoji : ''))
      .filter((e) => e && !seen.has(e) && seen.add(e));
  }, [data]);
  return <ReclaimPicker used={used} max={max} onDone={onDone} onCancel={onCancel} busy={busy} loading={isLoading} />;
};

export default AotdHand;
